import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CardContent,
  Input,
  Textarea,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Snackbar,
  FormLabel,
  CssBaseline,
} from '@mui/joy';
import {
  Paper,
  Container,
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
  Wallpaper as WallpaperIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { 
  getSiteSettings, 
  updateSiteSettings, 
  uploadSiteIcon,
  uploadWelcomeBackground
} from '../../main/API/settings';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';

export default function GeneralSettingsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [siteSettings, setSiteSettings] = useState({
    site_name: '',
    site_name_ar: '',
    site_name_zh: '',
    site_icon: '',
    welcome_background: '',
    cr_number: '',
    address: '',
    vat_number: '',
    bank_details: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  // Site icon upload states
  const [siteIconFile, setSiteIconFile] = useState(null);
  const [siteIconPreview, setSiteIconPreview] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  
  // Welcome background upload states
  const [welcomeBgFile, setWelcomeBgFile] = useState(null);
  const [welcomeBgPreview, setWelcomeBgPreview] = useState(null);
  const [uploadingWelcomeBg, setUploadingWelcomeBg] = useState(false);

  const loadSiteSettings = useCallback(async () => {
    console.log('GeneralSettingsScreen: loadSiteSettings called');
    if (!hasPermission(PERMISSIONS.SETTINGS_VIEW_PUBLIC)) {
      return;
    }
    setLoading(true);
    try {
      const settings = await getSiteSettings();
      setSiteSettings(prev => ({ ...prev, ...settings }));
      // Set preview if site_icon exists
      if (settings.site_icon) {
        setSiteIconPreview(settings.site_icon);
      }
      // Set preview if welcome_background exists
      if (settings.welcome_background) {
        setWelcomeBgPreview(settings.welcome_background);
      }
    } catch (error) {
      console.error('Failed to load site settings:', error);
      setResponseMessage(t('settingsPages.common.siteLoadFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, t]);

  // Load site settings on component mount
  useEffect(() => {
    if (!initialLoad) {
      console.log('GeneralSettingsScreen: Initial load starting');
      setInitialLoad(true);
      loadSiteSettings();
    }
  }, [initialLoad, loadSiteSettings]);

  const handleSiteSettingsChange = (field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSiteSettings = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_SITE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    setSaving(true);
    try {
      // Use bulk update API for site settings
      const dataToUpdate = {
        site_name: siteSettings.site_name || '',
        site_name_ar: siteSettings.site_name_ar || '',
        site_name_zh: siteSettings.site_name_zh || '',
        ...(siteSettings.site_icon && { site_icon: siteSettings.site_icon }),
        ...(siteSettings.welcome_background && { welcome_background: siteSettings.welcome_background })
      };
      await updateSiteSettings(dataToUpdate);
      setResponseMessage(t('settingsPages.common.generalSettingsUpdated'));
      setSuccessNotification(true);
      await loadSiteSettings();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (error) {
      console.error('Failed to update site settings:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.common.siteUpdateFailed'));
      setFailedNotification(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompanyProfile = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_SITE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    setSavingCompany(true);
    try {
      await updateSiteSettings({
        cr_number: siteSettings.cr_number || '',
        address: siteSettings.address || '',
        vat_number: siteSettings.vat_number || '',
        bank_details: siteSettings.bank_details || '',
      });
      setResponseMessage(t('settingsPages.general.companyProfileUpdated'));
      setSuccessNotification(true);
      await loadSiteSettings();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (error) {
      console.error('Failed to update company profile:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.common.siteUpdateFailed'));
      setFailedNotification(true);
    } finally {
      setSavingCompany(false);
    }
  };

  // Handle site icon file selection
  const handleSiteIconFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.ico'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setResponseMessage(t('settingsPages.common.invalidImageIcon'));
      setFailedNotification(true);
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setResponseMessage(t('settingsPages.common.fileTooLarge5mb'));
      setFailedNotification(true);
      return;
    }

    setSiteIconFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setSiteIconPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle site icon upload
  const handleUploadSiteIcon = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_FILES_UPLOAD)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    if (!siteIconFile) {
      setResponseMessage(t('settingsPages.common.selectFileFirst'));
      setFailedNotification(true);
      return;
    }

    setUploadingIcon(true);
    setResponseMessage('');
    
    try {
      const result = await uploadSiteIcon(siteIconFile);
      
      // Update site settings with the returned URL
      setSiteSettings(prev => ({
        ...prev,
        site_icon: result.url || result.site_icon
      }));
      
      setResponseMessage(t('settingsPages.common.siteIconUploadSuccess'));
      setSuccessNotification(true);
      
      // Update favicon immediately
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = result.url || result.site_icon;
      }
      
      // Reset file selection after successful upload
      setSiteIconFile(null);
      
      // Reload settings to get the updated icon URL
      await loadSiteSettings();
      
      // Trigger settings update event for other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (error) {
      console.error('Failed to upload site icon:', error);
      setResponseMessage(error.message || 'Failed to upload site icon');
      setFailedNotification(true);
    } finally {
      setUploadingIcon(false);
    }
  };

  // Handle welcome background file selection
  const handleWelcomeBgFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setResponseMessage('Please select a valid image file (jpg, png, gif, svg, webp)');
      setFailedNotification(true);
      return;
    }

    // Validate file size (max 10MB for background images)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setResponseMessage(t('settingsPages.common.fileTooLarge10mb'));
      setFailedNotification(true);
      return;
    }

    setWelcomeBgFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setWelcomeBgPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle welcome background upload
  const handleUploadWelcomeBackground = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_FILES_UPLOAD)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    if (!welcomeBgFile) {
      setResponseMessage(t('settingsPages.common.selectFileFirst'));
      setFailedNotification(true);
      return;
    }

    setUploadingWelcomeBg(true);
    setResponseMessage('');
    
    try {
      const result = await uploadWelcomeBackground(welcomeBgFile);
      
      // Update site settings with the returned URL
      setSiteSettings(prev => ({
        ...prev,
        welcome_background: result.url || result.welcome_background
      }));
      
      setResponseMessage(t('settingsPages.common.welcomeBgUploadSuccess'));
      setSuccessNotification(true);
      
      // Reset file selection after successful upload
      setWelcomeBgFile(null);
      
      // Reload settings to get the updated background URL
      await loadSiteSettings();
      
      // Trigger settings update event for other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (error) {
      console.error('Failed to upload welcome background:', error);
      setResponseMessage(error.message || t('settingsPages.common.welcomeBgUploadFailed'));
      setFailedNotification(true);
    } finally {
      setUploadingWelcomeBg(false);
    }
  };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
        <Box component="main" sx={{ overflow: "auto", pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <MuiTypography variant="h4" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                {t('settingsPages.general.pageTitle')}
              </MuiTypography>
              <MuiTypography variant="body1" color="#6B7280" sx={{ fontSize: '1.1rem' }}>
                {t('settingsPages.general.pageSubtitle')}
              </MuiTypography>
            </Box>

            {/* General Settings Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                mb: 4,
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <MuiTypography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
                    {t('settingsPages.general.pageTitle')}
                  </MuiTypography>
                  <MuiTypography variant="body2" color="#6B7280">
                    {t('settingsPages.general.pageSubtitle')}
                  </MuiTypography>
                </Box>
                
                <Grid container spacing={3}>
                  {/* Site Information Card */}
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 3, 
                        border: '1px solid #E5E7EB',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          border: '1px solid #D1D5DB'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: '#EEF2FF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <InfoIcon sx={{ color: '#4F46E5', fontSize: 24 }} />
                          </Box>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                            {t('settingsPages.hub.siteInformation')}
                          </Typography>
                        </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.siteNameEn')}</FormLabel>
                      <Input
                        fullWidth
                        value={siteSettings.site_name || ''}
                        onChange={(e) => handleSiteSettingsChange('site_name', e.target.value)}
                        placeholder={t('settingsPages.hub.siteNameEnPlaceholder')}
                        slotProps={{ input: { maxLength: 255 } }}
                        sx={{ mt: 1 }}
                      />
                      <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {t('settingsPages.hub.siteNameEnHint')}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.siteNameAr')}</FormLabel>
                      <Input
                        fullWidth
                        value={siteSettings.site_name_ar || ''}
                        onChange={(e) => handleSiteSettingsChange('site_name_ar', e.target.value)}
                        placeholder={t('settingsPages.hub.siteNameArPlaceholder')}
                        slotProps={{ input: { maxLength: 255, dir: 'rtl' } }}
                        sx={{ mt: 1 }}
                      />
                      <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {t('settingsPages.hub.siteNameArHint')}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.siteNameZh')}</FormLabel>
                      <Input
                        fullWidth
                        value={siteSettings.site_name_zh || ''}
                        onChange={(e) => handleSiteSettingsChange('site_name_zh', e.target.value)}
                        placeholder={t('settingsPages.hub.siteNameZhPlaceholder')}
                        slotProps={{ input: { maxLength: 255 } }}
                        sx={{ mt: 1 }}
                      />
                      <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {t('settingsPages.hub.siteNameZhHint')}
                      </Typography>
                    </Box>
                    
                    <PermissionGate permissions={[PERMISSIONS.SETTINGS_UPDATE_SITE]}>
                      <Button
                        variant="contained"
                        startDecorator={saving ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <SaveIcon />}
                        onClick={handleSaveSiteSettings}
                        disabled={saving || loading}
                        fullWidth
                        sx={{ mt: 'auto' }}
                      >
                        {saving ? t('settingsPages.common.saving') : t('settingsPages.hub.saveSettings')}
                      </Button>
                    </PermissionGate>
                      </CardContent>
                    </Paper>
                  </Grid>

                  {/* Site Icon Card */}
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 3, 
                        border: '1px solid #E5E7EB',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          border: '1px solid #D1D5DB'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: '#ECFDF5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <PaletteIcon sx={{ color: '#10B981', fontSize: 24 }} />
                          </Box>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                            {t('settingsPages.hub.siteIcon')}
                          </Typography>
                        </Box>
                    
                    <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                      <Box sx={{ mb: 2 }}>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.uploadSiteIcon')}</FormLabel>
                        <input
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                          style={{ display: 'none' }}
                          id="site-icon-upload"
                          type="file"
                          onChange={handleSiteIconFileChange}
                          disabled={uploadingIcon}
                        />
                        <label htmlFor="site-icon-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startDecorator={<UploadIcon />}
                            fullWidth
                            disabled={uploadingIcon}
                            sx={{ mt: 1 }}
                          >
                            {siteIconFile ? siteIconFile.name : t('settingsPages.hub.chooseImageFile')}
                          </Button>
                        </label>
                        <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          {t('settingsPages.hub.iconFormatsHint')}
                        </Typography>
                      </Box>
                    </PermissionGate>
                    
                    {/* Preview */}
                    {siteIconPreview && (
                      <Box sx={{ 
                        mt: 2, 
                        mb: 2, 
                        textAlign: 'center',
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Avatar
                          src={siteIconPreview}
                          sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }}
                        />
                        {siteIconFile && (
                          <Typography level="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {siteIconFile.name}
                          </Typography>
                        )}
                        {siteIconFile && (
                          <Typography level="body3" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                            {(siteIconFile.size / 1024).toFixed(2)} KB
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Upload Button */}
                    {siteIconFile && (
                      <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                        <Button
                          variant="contained"
                          color="success"
                          startDecorator={uploadingIcon ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <UploadIcon />}
                          onClick={handleUploadSiteIcon}
                          disabled={uploadingIcon}
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          {uploadingIcon ? t('settingsPages.common.uploading') : t('settingsPages.hub.uploadIcon')}
                        </Button>
                      </PermissionGate>
                    )}
                    
                    {/* Current Icon Display */}
                    {siteSettings.site_icon && !siteIconFile && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 1.5, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography level="body3" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                          {t('settingsPages.hub.currentIcon')}
                        </Typography>
                        <Input
                          fullWidth
                          value={siteSettings.site_icon}
                          onChange={(e) => handleSiteSettingsChange('site_icon', e.target.value)}
                          size="sm"
                          readOnly
                          sx={{ 
                            '& input': {
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              bgcolor: 'background.body'
                            }
                          }}
                        />
                      </Box>
                    )}
                      </CardContent>
                    </Paper>
                  </Grid>

                  {/* Welcome Background Card */}
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 3, 
                        border: '1px solid #E5E7EB',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          border: '1px solid #D1D5DB'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: '#FFFBEB',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <WallpaperIcon sx={{ color: '#F59E0B', fontSize: 24 }} />
                          </Box>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                            {t('settingsPages.hub.welcomeBackground')}
                          </Typography>
                        </Box>
                    
                    <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                      <Box sx={{ mb: 2 }}>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.uploadBackground')}</FormLabel>
                        <input
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                          style={{ display: 'none' }}
                          id="welcome-bg-upload"
                          type="file"
                          onChange={handleWelcomeBgFileChange}
                          disabled={uploadingWelcomeBg}
                        />
                        <label htmlFor="welcome-bg-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startDecorator={<UploadIcon />}
                            fullWidth
                            disabled={uploadingWelcomeBg}
                            sx={{ mt: 1 }}
                          >
                            {welcomeBgFile ? welcomeBgFile.name : t('settingsPages.hub.chooseBackgroundImage')}
                          </Button>
                        </label>
                        <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          {t('settingsPages.hub.bgFormatsHint')}
                        </Typography>
                      </Box>
                    </PermissionGate>
                    
                    {/* Preview */}
                    {welcomeBgPreview && (
                      <Box sx={{ 
                        mt: 2, 
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <img
                          src={welcomeBgPreview}
                          alt={t('settingsPages.hub.welcomeBgPreviewAlt')}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        {welcomeBgFile && (
                          <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                            <Typography level="body2" sx={{ color: 'text.secondary' }}>
                              {welcomeBgFile.name}
                            </Typography>
                            <Typography level="body3" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                              {(welcomeBgFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    
                    {/* Upload Button */}
                    {welcomeBgFile && (
                      <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                        <Button
                          variant="contained"
                          color="warning"
                          startDecorator={uploadingWelcomeBg ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <UploadIcon />}
                          onClick={handleUploadWelcomeBackground}
                          disabled={uploadingWelcomeBg}
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          {uploadingWelcomeBg ? t('settingsPages.common.uploading') : t('settingsPages.hub.uploadBackgroundBtn')}
                        </Button>
                      </PermissionGate>
                    )}
                    
                    {/* Current Background Display */}
                    {siteSettings.welcome_background && !welcomeBgFile && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 1.5, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography level="body3" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                          {t('settingsPages.hub.currentBackground')}
                        </Typography>
                        <Input
                          fullWidth
                          value={siteSettings.welcome_background}
                          onChange={(e) => handleSiteSettingsChange('welcome_background', e.target.value)}
                          size="sm"
                          readOnly
                          sx={{ 
                            '& input': {
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              bgcolor: 'background.body'
                            }
                          }}
                        />
                      </Box>
                    )}
                      </CardContent>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Company Profile Section */}
                <Box sx={{ mt: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <MuiTypography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
                      {t('settingsPages.general.companySectionTitle')}
                    </MuiTypography>
                    <MuiTypography variant="body2" color="#6B7280">
                      {t('settingsPages.general.companySectionSubtitle')}
                    </MuiTypography>
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #E5E7EB',
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: '#EFF6FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                          }}
                        >
                          <BusinessIcon sx={{ color: '#2563EB', fontSize: 24 }} />
                        </Box>
                        <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                          {t('settingsPages.general.companyProfile')}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            {t('settingsPages.general.crNumber')}
                          </FormLabel>
                          <Input
                            fullWidth
                            value={siteSettings.cr_number || ''}
                            onChange={(e) => handleSiteSettingsChange('cr_number', e.target.value)}
                            placeholder={t('settingsPages.general.crNumberPlaceholder')}
                            slotProps={{ input: { maxLength: 255 } }}
                            sx={{ mt: 1 }}
                          />
                          <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {t('settingsPages.general.crNumberHint')}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            {t('settingsPages.general.vatNumber')}
                          </FormLabel>
                          <Input
                            fullWidth
                            value={siteSettings.vat_number || ''}
                            onChange={(e) => handleSiteSettingsChange('vat_number', e.target.value)}
                            placeholder={t('settingsPages.general.vatNumberPlaceholder')}
                            slotProps={{ input: { maxLength: 255 } }}
                            sx={{ mt: 1 }}
                          />
                          <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {t('settingsPages.general.vatNumberHint')}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            {t('settingsPages.general.address')}
                          </FormLabel>
                          <Textarea
                            fullWidth
                            minRows={3}
                            value={siteSettings.address || ''}
                            onChange={(e) => handleSiteSettingsChange('address', e.target.value)}
                            placeholder={t('settingsPages.general.addressPlaceholder')}
                            sx={{ mt: 1 }}
                          />
                          <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {t('settingsPages.general.addressHint')}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            {t('settingsPages.general.bankDetails')}
                          </FormLabel>
                          <Textarea
                            fullWidth
                            minRows={4}
                            value={siteSettings.bank_details || ''}
                            onChange={(e) => handleSiteSettingsChange('bank_details', e.target.value)}
                            placeholder={t('settingsPages.general.bankDetailsPlaceholder')}
                            sx={{ mt: 1 }}
                          />
                          <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {t('settingsPages.general.bankDetailsHint')}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <PermissionGate permissions={[PERMISSIONS.SETTINGS_UPDATE_SITE]}>
                            <Button
                              variant="contained"
                              startDecorator={
                                savingCompany ? (
                                  <CircularProgress sx={{ width: 20, height: 20 }} />
                                ) : (
                                  <SaveIcon />
                                )
                              }
                              onClick={handleSaveCompanyProfile}
                              disabled={savingCompany || loading}
                              sx={{ maxWidth: 320 }}
                            >
                              {savingCompany
                                ? t('settingsPages.common.saving')
                                : t('settingsPages.general.saveCompanyProfile')}
                            </Button>
                          </PermissionGate>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Paper>
                </Box>
              </CardContent>
            </Paper>

      {/* Success Notification */}
      <Snackbar
        autoHideDuration={3000}
        open={successNotification}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setSuccessNotification(false);
        }}
      >
        {responseMessage}
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        autoHideDuration={3000}
        open={failedNotification}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setFailedNotification(false);
        }}
      >
        {responseMessage}
      </Snackbar>
          </Container>
        </Box>
      </Box>
    </DirectionalMaterialThemeProvider>
  );
}

